import { Box, Stack } from "@mui/system";
import KeywordsSearchItemsCard from "./KeywordsSearchItemsCard";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { formatNumber } from "../../../../../../../../../utils/helpers";
import GoogleAds from "../../../../../../../../../assets/googleAD.svg";
import BingAds from "../../../../../../../../../assets/bingAD.svg";
import MetaAds from "../../../../../../../../../assets/metaAD.svg";

type StatusColor = "success" | "warning" | "error";
type Props = {
  searchValue: string | null;
  sortConfig: {
    field: string | null;
    direction: "asc" | "desc";
  };
};

const KeywordsSearchItems = ({ searchValue, sortConfig }: Props) => {
  const { keywordsQueryData } = useSelector(
    (state: any) => state.businessLeaderDashBoard,
  );

  const getBrandIcon = (trafficSource: string | undefined | null) => {
    if (!trafficSource) return null;
    if (trafficSource === "g_ads_search") return GoogleAds;
    if (trafficSource === "bing_ads_search") return BingAds;
    return MetaAds;
  };

  const getTierNumber = (tier: string | undefined | null) => {
    if (!tier) return "";
    return tier.replace("tier_", "");
  };

  const keywordsData = keywordsQueryData?.query_output || [];
  const filteredData = searchValue
    ? keywordsData.filter((row: any) =>
        row?.kw_name?.toLowerCase().includes(searchValue.toLowerCase()),
      )
    : keywordsData;

  const sortedData = useMemo(() => {
    if (!sortConfig.field) return filteredData;

    const field = sortConfig.field;
    const direction = sortConfig.direction;

    return [...filteredData].sort((a: any, b: any) => {
      let aValue = a[field];
      let bValue = b[field];

      // Always push nulls/undefined to the bottom, regardless of direction
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Special case: parse tier strings into numbers
      if (field === "tier") {
        aValue = parseInt(aValue.replace("tier_", ""), 10);
        bValue = parseInt(bValue.replace("tier_", ""), 10);
      }

      let cmp: number;

      if (typeof aValue === "string" && typeof bValue === "string") {
        cmp = aValue.localeCompare(bValue);
      } else {
        cmp = aValue - bValue;
      }

      // Flip for descending
      return direction === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortConfig]);

  const highlightIMPShare = (impShare?: number | null): StatusColor => {
    const value = Number(impShare ?? 0);
    if (value <= 0.6) return "error";
    if (value <= 0.85) return "warning";
    return "success";
  };

  const highlightCVR = (cvr?: number | null): StatusColor => {
    const value = Number(cvr ?? 0);
    if (value < 0.07) return "error";
    if (value > 0.3) return "success";
    return "warning";
  };

  return (
    <>
      <Box>
        {sortedData.length === 0 ? (
          <Box
            sx={{
              height: "250px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            No Data to Display
          </Box>
        ) : (
          <Stack spacing={2}>
            {sortedData?.map((row: any, index: number) => {
              return (
                <KeywordsSearchItemsCard
                  key={`${index}`}
                  title={`${row?.kw_name} (${row?.matchtype})`}
                  brandIcon={getBrandIcon(row?.traffic_source)}
                  cost={`$${formatNumber(Math.round(row?.cost))}`}
                  impPercentage={`${Math.round(row?.imp_share * 100)}%`}
                  impColor={highlightIMPShare(row?.imp_share)}
                  ftd={`${formatNumber(Math.round(row?.FTD))}`}
                  cac={`$${formatNumber(Math.round(row?.CAC))}`}
                  cvrPercentage={`${Math.round(row?.CVR * 100)}%`}
                  cvrColor={highlightCVR(row?.CVR)}
                  tier={getTierNumber(row?.tier)}
                  adGroup={`${row?.adgroup_name}`}
                  campaign={`${row?.campaign_name}`}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default KeywordsSearchItems;
